import React, { useMemo, useState } from "react";
import { StatCard } from "../components/StatCard";
import { ActivityFilter, MetricFilter, FilterCard } from "../components/VisualFilter";
import { Trophy, Sparkles, ArrowUp, ArrowDown, Loader2 } from "lucide-react";
import { useApiResource } from "../api/useApi";
import { getImproveDetail, getImproveOverview } from "../api/resources";
import type { FrontendTile, TrendPoint } from "../api/types";
import { ACTIVITY_LABEL, ActivityId } from "../mockData";

/**
 * Progress — REAL data only (uploadImproveOverview / uploadImproveDetail, both computed
 * from the user's actual scorecards on the blended finalScore ruler). This page used to
 * blend API values with mock fallbacks (seeded phase table, fake PRs, hardcoded +6.4%
 * deltas, a decorative sparkline and a fabricated "AI summary") — every one of those is
 * gone. No data renders as an honest empty state, never as a plausible fake.
 */

const METRIC_LABELS: Record<string, { label: string; unit: string }> = {
  score: { label: "Score", unit: "/100" },
};

const techniqueLabel = (sportId?: string | null): string =>
  sportId ? ACTIVITY_LABEL[sportId as ActivityId] ?? sportId : "Unknown";

export default function Progress() {
  const [sport, setSport] = useState<string>("all");
  const [metric, setMetric] = useState<string>("score");

  const overview = useApiResource((apiFetch) => getImproveOverview(apiFetch), []);
  const detail = useApiResource(
    (apiFetch) => getImproveDetail(apiFetch, { activityId: sport }),
    [sport],
  );

  const tilesByTitle = useMemo(() => {
    const map: Record<string, FrontendTile> = {};
    for (const t of overview.data?.kpiTiles ?? []) map[t.title] = t;
    return map;
  }, [overview.data]);

  const avgQualityTile = tilesByTitle["Avg quality"];
  const bestScoreTile = tilesByTitle["Best score"];
  const streakTile = tilesByTitle["Streak"];
  const activePhasesTile = tilesByTitle["Active phases"];

  const phaseProgression = detail.data?.phaseProgression ?? [];
  const personalRecords = detail.data?.personalRecords ?? [];

  // Metric options come from what the API actually returned trends for — no mock series.
  const metricOptions = useMemo(() => {
    const keys = Object.keys(detail.data?.trends ?? {});
    return (keys.length > 0 ? keys : ["score"]).map((k) => ({
      key: k,
      label: METRIC_LABELS[k]?.label ?? k,
      unit: METRIC_LABELS[k]?.unit,
    }));
  }, [detail.data]);
  const activeMetric = metricOptions.some((m) => m.key === metric) ? metric : "score";
  const metricMeta = METRIC_LABELS[activeMetric] ?? { label: activeMetric, unit: "" };

  const trendData: TrendPoint[] = detail.data?.trends?.[activeMetric] ?? [];

  const sportEmoji =
    sport === "Golf" ? "🏌️" : sport === "Tennis" ? "🎾" : sport === "Cricket" ? "🏏" :
    sport === "Bowling" ? "🎳" : sport === "Movement" ? "🏃" : undefined;

  // A KPI delta renders only when the API computed one; 0 = "no delta", hidden.
  const deltaOf = (tile?: FrontendTile): number | undefined =>
    tile && tile.percentageValue !== 0 ? tile.percentageValue : undefined;

  const kpiValue = (tile?: FrontendTile): string | number =>
    overview.isLoading ? "…" : tile?.numberValue ?? "—";

  return (
    <div className="space-y-4">
      <FilterCard
        countActive={(sport !== "all" ? 1 : 0) + (activeMetric !== "score" ? 1 : 0)}
        summary={[
          { label: "Activity", value: sport === "all" ? "All" : sport, emoji: sportEmoji },
          { label: "Metric", value: metricMeta.label },
        ]}
      >
        <ActivityFilter value={sport} onChange={setSport} granularity="family" />
        <MetricFilter options={metricOptions} value={activeMetric} onChange={setMetric} />
      </FilterCard>

      {/* KPIs — /platform/uploadImproveOverview, computed from real uploads + scorecards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label="Avg quality"
          value={kpiValue(avgQualityTile)}
          unit="/100"
          delta={deltaOf(avgQualityTile)}
          intent="positive"
        />
        <StatCard
          label="Best score"
          value={kpiValue(bestScoreTile)}
          unit="PR"
          delta={deltaOf(bestScoreTile)}
          intent="positive"
        />
        <StatCard
          label="Streak"
          value={overview.isLoading ? "…" : `${streakTile?.numberValue ?? 0}w`}
          unit="consecutive weeks"
          delta={deltaOf(streakTile)}
          intent="positive"
        />
        <StatCard
          label="Active phases"
          value={overview.isLoading ? "…" : `${activePhasesTile?.numberValue ?? 0}/10`}
          unit="latest session"
        />
      </div>

      {/* Trend on the selected metric — real weekly averages; weeks without data are gaps */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">
              {metricMeta.label} · 12-week trend
            </div>
            <div className="text-xs text-slate-500">
              {sport === "all" ? "All activities" : sport}
            </div>
          </div>
          <div className="text-xs text-slate-500">Weekly averages · blended score</div>
        </div>
        {detail.isLoading ? (
          <div className="flex items-center gap-2 py-10 justify-center text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : trendData.length >= 2 ? (
          <BigTrend unit={metricMeta.unit} data={trendData} />
        ) : (
          <div className="py-10 text-center text-sm text-slate-500">
            {trendData.length === 1 ? (
              <>
                One scored week so far ({trendData[0].wk}:{" "}
                <span className="font-semibold text-slate-700">{trendData[0].value}</span>) — a
                trend needs two.
              </>
            ) : (
              <>No scored sessions in the last 12 weeks{sport !== "all" && <> for {sport}</>}.</>
            )}
          </div>
        )}
      </div>

      {(phaseProgression.length > 0 || personalRecords.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {phaseProgression.length > 0 && (
            <div className="card p-5 lg:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Per-phase progression</div>
                  <div className="text-xs text-slate-500">Last 4 sessions vs prior 4</div>
                </div>
                <div className="text-xs text-slate-500">{sport === "all" ? "All activities" : sport}</div>
              </div>
              <ul className="space-y-3">
                {phaseProgression.map((p) => {
                  const delta = p.now - p.prev;
                  const up = delta >= 0;
                  return (
                    <li key={p.phase} className="flex items-center gap-4">
                      <div className="w-10 shrink-0 text-xs font-semibold text-el-700">{p.phase}</div>
                      <div className="flex-1 min-w-0">
                        <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="absolute top-0 left-0 h-2 bg-slate-300 rounded-full" style={{ width: `${p.prev}%` }} />
                          <div className="absolute top-0 left-0 h-2 bg-gradient-to-r from-el-400 to-el-600 rounded-full" style={{ width: `${p.now}%` }} />
                        </div>
                      </div>
                      <div className="w-20 text-right">
                        <div className="text-sm font-medium text-slate-900">{p.now}</div>
                        {delta !== 0 && (
                          <div className={`text-[0.7rem] flex items-center justify-end gap-0.5 ${up ? "text-el-700" : "text-rose-600"}`}>
                            {up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                            {Math.abs(delta).toFixed(1)}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-4 flex items-center gap-3 text-[0.7rem] text-slate-500 border-t border-slate-100 pt-3">
                <span className="inline-flex items-center gap-1"><span className="h-1.5 w-3 rounded-sm bg-slate-300" /> prior block</span>
                <span className="inline-flex items-center gap-1"><span className="h-1.5 w-3 rounded-sm bg-gradient-to-r from-el-400 to-el-600" /> now</span>
              </div>
            </div>
          )}

          {personalRecords.length > 0 && (
            <div className="card p-5">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="h-4 w-4 text-el-600" />
                <div className="text-sm font-semibold text-slate-900">Personal records</div>
              </div>
              <ul className="space-y-3">
                {personalRecords.map((pr) => (
                  <li key={pr.id} className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-sm text-slate-900 font-medium truncate">
                        {techniqueLabel(pr.context)}
                      </div>
                      <div className="text-base font-semibold text-el-700">{pr.value}</div>
                    </div>
                    <div className="text-[0.7rem] text-slate-500 truncate">{pr.metric}</div>
                    <div className="text-[0.65rem] text-slate-400">{pr.achievedAt}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Trend summary — derived from the real series above, nothing generated or fabricated */}
      {trendData.length >= 2 && (
        <div className="card p-5 bg-gradient-to-br from-white to-el-50/40">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-el-100 text-el-700 flex items-center justify-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">Your trend, in one line</div>
              <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">
                <TrendSummary sport={sport} metricLabel={metricMeta.label} data={trendData} />
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** One sentence computed from the real series: newest week vs the earliest in the window. */
function TrendSummary({
  sport,
  metricLabel,
  data,
}: {
  sport: string;
  metricLabel: string;
  data: TrendPoint[];
}) {
  const first = data[0];
  const last = data[data.length - 1];
  const delta = Math.round((last.value - first.value) * 10) / 10;
  const scope = sport === "all" ? "Across all activities" : `On ${sport}`;
  if (Math.abs(delta) < 1) {
    return (
      <>
        {scope}, your {metricLabel.toLowerCase()} is holding steady around{" "}
        <span className="font-medium">{last.value}</span> ({first.wk} → {last.wk}).
      </>
    );
  }
  return (
    <>
      {scope}, your {metricLabel.toLowerCase()} moved from{" "}
      <span className="font-medium">{first.value}</span> ({first.wk}) to{" "}
      <span className="font-medium">{last.value}</span> ({last.wk}) —{" "}
      {delta > 0 ? "up" : "down"} {Math.abs(delta)} points.
    </>
  );
}

function BigTrend({ unit, data }: { unit: string; data: TrendPoint[] }) {
  const w = 800, h = 200, pad = { l: 36, r: 12, t: 12, b: 28 };

  const values = data.map((d) => d.value);
  const max = Math.max(...values), min = Math.min(...values);
  const yPad = (max - min) * 0.2 || 1;
  const yMin = min - yPad, yMax = max + yPad;
  const sx = (i: number) => pad.l + (i / (data.length - 1)) * (w - pad.l - pad.r);
  const sy = (v: number) => pad.t + (1 - (v - yMin) / (yMax - yMin)) * (h - pad.t - pad.b);
  const yTicks = 4;
  const yStep = (yMax - yMin) / yTicks;

  const line = data.map((d, i) => `${i === 0 ? "M" : "L"} ${sx(i)} ${sy(d.value)}`).join(" ");
  const area =
    `M ${sx(0)} ${h - pad.b} ` +
    data.map((d, i) => `L ${sx(i)} ${sy(d.value)}`).join(" ") +
    ` L ${sx(data.length - 1)} ${h - pad.b} Z`;

  const fmt = (v: number) => Math.round(v).toString();

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
      <defs>
        <linearGradient id="trend-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"  stopColor="#10b981" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
      </defs>

      {Array.from({ length: yTicks + 1 }).map((_, i) => {
        const yv = yMin + i * yStep;
        const y = sy(yv);
        return (
          <g key={i}>
            <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="#eef2ef" />
            <text x={pad.l - 6} y={y + 3} fontSize="10" textAnchor="end" fill="#94a3b8">
              {fmt(yv)}{unit !== "/100" ? unit : ""}
            </text>
          </g>
        );
      })}

      <path d={area} fill="url(#trend-fill)" />
      <path d={line} fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

      {data.map((d, i) => (
        <g key={d.wk}>
          <circle cx={sx(i)} cy={sy(d.value)} r="2.6" fill="#10b981" />
          <text x={sx(i)} y={h - pad.b + 14} fontSize="9.5" textAnchor="middle" fill="#94a3b8">
            {d.wk}
          </text>
        </g>
      ))}
    </svg>
  );
}
